"use client";

import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  BrainCircuitIcon,
  ScaleIcon,
  ShieldCheckIcon,
  TagIcon,
} from "lucide-react";

import { Container } from "@/app/(marketing)/_components/ui/container";

interface Feature {
  icon: LucideIcon;
  title: string;
  description: string;
  highlights: string[];
}

const features: Feature[] = [
  {
    icon: BrainCircuitIcon,
    title: "Domain Expert Network",
    description:
      "Access verified experts across engineering, medicine, law, finance, and 200+ domains.",
    highlights: [
      "Higher signal on niche tasks generic crowdworkers can't score",
      "Profiles deepen with each interaction, no cold matching",
      "Reach hard-to-find audiences across 195 countries",
    ],
  },
  {
    icon: TagIcon,
    title: "Data Annotation & Labeling",
    description:
      "Expert-grade annotation for complex tasks with higher inter-annotator agreement.",
    highlights: [
      "Catch labeling failures crowdworkers miss",
      "Defensible labels for regulated domains",
      "Fewer relabel cycles, faster dataset iteration",
    ],
  },
  {
    icon: ScaleIcon,
    title: "Training & Alignment",
    description:
      "Generate preference data, RLHF signals, and instruction-tuning datasets from verified domain experts.",
    highlights: [
      "Preference data your model actually learns from",
      "Instruction tuning grounded in real practice",
      "Reduce hallucinations on domain-specific prompts",
    ],
  },
  {
    icon: ShieldCheckIcon,
    title: "Evaluation & Safety",
    description: "Run human evaluations at scale across specialized domains.",
    highlights: [
      "Catch capability gaps before launch",
      "Audit trail for accuracy, safety, and helpfulness claims",
      "Production-scale eval throughput on demand",
    ],
  },
];

export function AIFeatures() {
  return (
    <section className="py-20">
      <Container>
        {/* Section header */}
        <div className="mb-16 flex flex-col items-center gap-8">
          <div className="bg-marketing-card-blue rounded-t-[10px] px-2.5 py-1.5">
            <p className="text-marketing-blue font-mono text-sm tracking-[-0.28px]">
              Capabilities
            </p>
          </div>
          <h2 className="text-marketing-text dark:text-marketing-cream max-w-[700px] text-center text-3xl leading-[1.1] font-medium tracking-[-1.5px] md:text-4xl lg:text-[50px]">
            Build better AI with human intelligence
          </h2>
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, transform: "translateY(20px)" }}
              whileInView={{ opacity: 1, transform: "translateY(0px)" }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="border-marketing-border bg-marketing-cream-light rounded-[20px] border p-8"
            >
              <div className="bg-marketing-card-blue mb-4 flex h-12 w-12 items-center justify-center rounded-full">
                <feature.icon className="text-marketing-blue h-6 w-6" />
              </div>
              <h3 className="text-marketing-text mb-3 text-xl font-medium">
                {feature.title}
              </h3>
              <p className="text-marketing-text-muted mb-6 text-base leading-relaxed">
                {feature.description}
              </p>
              <div className="flex flex-col gap-2">
                {feature.highlights.map((highlight) => (
                  <div key={highlight} className="flex items-center gap-2.5">
                    <div className="bg-marketing-blue h-1.5 w-1.5 shrink-0 rounded-full" />
                    <p className="text-marketing-text text-sm font-medium">
                      {highlight}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}
